/**
 * Theme MUI theo bộ màu chủ đạo TLC:
 * forest #0b3633, teal #145a59, coffee orange #da833c,
 * cream #fff9f4, signal red #bc2434, harvest gold #f4df9c.
 */
import { createTheme, alpha } from '@mui/material/styles'

const TLC = {
  forest: '#0b3633',
  teal: '#145a59',
  copper: '#da833c',
  cream: '#fff9f4',
  red: '#bc2434',
  gold: '#f4df9c',
  primaryMain: '#145a59',
  primaryLight: '#2a7370',
  primaryDark: '#0b3633',
  secondaryMain: '#da833c',
  secondaryLight: '#f4df9c',
  secondaryDark: '#a95e25',
  bgDefault: '#fbf6ef',
  bgSoft: '#fff9f4',
  paper: '#ffffff',
  ink: '#142421',
  muted: '#66746f',
} as const

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: TLC.primaryMain,
      light: TLC.primaryLight,
      dark: TLC.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: TLC.secondaryMain,
      light: TLC.secondaryLight,
      dark: TLC.secondaryDark,
      contrastText: '#1a1f1c',
    },
    success: {
      main: '#2f8f63',
      light: '#6bbf91',
      dark: '#1d6546',
    },
    warning: {
      main: TLC.copper,
      light: TLC.gold,
      dark: TLC.secondaryDark,
    },
    error: {
      main: TLC.red,
      light: '#d34e5a',
      dark: '#8d1825',
    },
    info: {
      main: TLC.teal,
      light: '#4c8d89',
      dark: TLC.forest,
      contrastText: '#ffffff',
    },
    background: {
      default: TLC.bgDefault,
      paper: TLC.paper,
    },
    text: {
      primary: TLC.ink,
      secondary: TLC.muted,
      disabled: '#94a3b8',
    },
    divider: alpha(TLC.forest, 0.12),
  },
  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '0.9375rem',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.8125rem',
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: '#5c665f',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.08)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.06)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.06)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.2)',
  ],
  components: {
    // Chrome (M120+) chặn `aria-hidden` trên #root khi phần tử con còn focus:
    // lúc ĐÓNG dialog, MUI v5 trả focus về nút mở TRƯỚC khi gỡ aria-hidden
    // (cleanup FocusTrap chạy trước ModalManager) → console warning "Blocked
    // aria-hidden ... descendant retained focus" mỗi lần đóng dialog. Tắt
    // restore-focus mặc định cho mọi Dialog (trade-off nhỏ: sau khi đóng,
    // keyboard focus không quay lại nút mở). Fix triệt để chỉ có ở MUI v6+.
    MuiDialog: {
      defaultProps: {
        disableRestoreFocus: true,
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--app-radius-md': '14px',
          '--app-radius-lg': '18px',
          '--app-sidebar-icon': alpha(TLC.cream, 0.82),
          '--app-sidebar-active-icon': TLC.gold,
          '--app-sidebar-active-border': TLC.secondaryMain,
          '--app-sidebar-active-bg-solid': '#edf6f4',
          '--app-table-head-bg': '#fff4e9',
          '--app-table-head-text': TLC.forest,
          '--app-table-row-alt': 'rgba(244, 223, 156, 0.20)',
          '--app-table-row-hover': 'rgba(20, 90, 89, 0.09)',
          '--app-table-border': 'rgba(11, 54, 51, 0.13)',
          '--n2nai-primary-main': TLC.primaryMain,
          '--n2nai-primary-dark': TLC.primaryDark,
          '--n2nai-secondary-main': TLC.secondaryMain,
          '--n2nai-secondary-dark': TLC.secondaryDark,
          '--n2nai-bg-default': TLC.bgDefault,
          '--tlc-forest': TLC.forest,
          '--tlc-teal': TLC.teal,
          '--tlc-copper': TLC.copper,
          '--tlc-cream': TLC.cream,
          '--tlc-red': TLC.red,
          '--tlc-gold': TLC.gold,
        },
        '.theme-light': {
          '--app-bg': TLC.bgDefault,
          '--app-sidebar-bg': TLC.cream,
          '--app-sidebar-active-bg': `linear-gradient(90deg, ${alpha(TLC.teal, 0.14)}, ${alpha(TLC.gold, 0.34)})`,
          '--app-sidebar-text': TLC.primaryDark,
          '--app-sidebar-muted': '#7e8b86',
          '--app-sidebar-icon': '#57716d',
          '--app-sidebar-active-icon': TLC.copper,
          '--app-sidebar-hover-bg': 'rgba(20, 90, 89, 0.075)',
          '--app-sidebar-active-text': TLC.primaryDark,
          '--app-topbar-bg': `linear-gradient(90deg, ${TLC.forest} 0%, ${TLC.teal} 64%, #196967 100%)`,
          '--app-topbar-text': TLC.cream,
          '--app-card-bg': TLC.paper,
          '--app-border-subtle': 'rgba(11, 54, 51, 0.13)',
          '--app-shadow-soft': '0 10px 30px -24px rgba(11, 54, 51, 0.55)',
        },
        '.theme-dark': {
          '--app-bg': '#081f1d',
          '--app-sidebar-bg': '#0b2624',
          '--app-sidebar-active-bg': TLC.primaryDark,
          '--app-sidebar-text': TLC.cream,
          '--app-sidebar-muted': '#b9c4bf',
          '--app-topbar-bg': TLC.forest,
          '--app-card-bg': '#0f2b28',
          '--app-border-subtle': 'rgba(15, 23, 42, 0.7)',
          '--app-shadow-soft': '0 1px 4px rgba(15, 23, 42, 0.85)',
        },
        html: { height: '100%', width: '100%' },
        body: {
          height: '100%',
          width: '100%',
          margin: 0,
          backgroundColor: 'var(--app-bg)',
          display: 'block',
        },
        '#root': { height: '100%', width: '100%', display: 'block' },
        '.app-shell': {
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--app-bg)',
        },
        '.MuiAppBar-root': {
          backgroundImage: `linear-gradient(90deg, ${TLC.forest} 0%, ${TLC.teal} 58%, #1a6e6b 100%)`,
          borderBottom: `1px solid ${alpha(TLC.gold, 0.26)}`,
          boxShadow: '0 8px 28px -24px rgba(11, 54, 51, 0.65)',
        },
        '.page-header': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        },
        '.page-header-title': {
          fontSize: '1.125rem',
          fontWeight: 600,
        },
        '.page-header-subtitle': {
          fontSize: '0.875rem',
          color: TLC.muted,
        },
        '.page-card': {
          backgroundColor: 'var(--app-card-bg)',
          borderRadius: 'var(--app-radius-lg)',
          boxShadow: 'var(--app-shadow-soft)',
          border: `1px solid var(--app-border-subtle)`,
        },
        '.page-table-card': {
          backgroundColor: 'var(--app-card-bg)',
          borderRadius: 'var(--app-radius-lg)',
          boxShadow: 'var(--app-shadow-soft)',
          border: `1px solid var(--app-border-subtle)`,
          overflow: 'hidden',
        },
        '.status-chip-warning': {
          backgroundColor: alpha(TLC.copper, 0.13),
          color: TLC.secondaryDark,
          borderRadius: '999px',
          fontSize: '0.75rem',
          padding: '4px 10px',
          fontWeight: 600,
        },
        '.status-chip-primary': {
          backgroundColor: alpha(TLC.primaryMain, 0.1),
          color: TLC.primaryDark,
          borderRadius: '999px',
          fontSize: '0.75rem',
          padding: '4px 10px',
          fontWeight: 600,
        },
        '.btn-primary-rounded': {
          borderRadius: '999px',
          paddingInline: '18px',
          fontWeight: 600,
        },
        // Fix iOS Safari auto-zoom on input focus: nếu font-size < 16px, Safari
        // tự zoom viewport khi user tap input/combobox/datepicker. Đặt ở
        // CssBaseline với !important để override sx custom (compactInputSx font-size
        // 0.8125rem) + inline style trong inputProps.
        // Tablet/desktop (>= 600px) vẫn giữ size dense theo sx component.
        '@media (max-width: 599.95px)': {
          '.MuiInputBase-input, input, textarea': {
            fontSize: '16px !important',
          },
          // Exception cho TablePagination select (Rows per page: All/20/50/100):
          // dropdown này không zoom iOS (native <select>), giữ font dense để UI
          // toolbar không chiếm chỗ. Specificity 0,2,0 + !important thắng rule
          // trên (0,1,0 + !important).
          '.MuiTablePagination-root .MuiTablePagination-select': {
            fontSize: '0.65rem !important',
          },
          '.MuiTablePagination-root .MuiTablePagination-selectLabel, .MuiTablePagination-root .MuiTablePagination-displayedRows': {
            fontSize: '0.65rem !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          fontWeight: 700,
          letterSpacing: '0.01em',
          padding: '7px 18px',
          boxShadow: 'none',
          transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(TLC.primaryMain, 0.18)}`,
            transform: 'translateY(-1px)',
          },
          '&.Mui-disabled': {
            transform: 'none',
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundImage: `linear-gradient(180deg, ${alpha('#ffffff', 0.08)}, ${alpha('#000000', 0.04)})`,
          '&:hover': {
            boxShadow: `0 5px 14px ${alpha(TLC.primaryMain, 0.26)}`,
          },
        },
        outlined: {
          borderWidth: 1,
          backgroundColor: alpha(TLC.paper, 0.68),
          '&:hover': {
            borderWidth: 1,
            backgroundColor: alpha(TLC.primaryMain, 0.055),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 10px 30px -26px rgba(11, 54, 51, 0.5)',
          border: `1px solid ${alpha(TLC.forest, 0.1)}`,
          '&:hover': {
            boxShadow: '0 18px 36px -28px rgba(11, 54, 51, 0.65)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 10px 30px -26px rgba(11, 54, 51, 0.42)',
          border: `1px solid ${alpha(TLC.forest, 0.1)}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 8px 28px -24px rgba(11, 54, 51, 0.7)',
          borderBottom: `1px solid ${alpha(TLC.gold, 0.28)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: `1px solid ${alpha(TLC.forest, 0.13)}`,
          boxShadow: '8px 0 28px -30px rgba(11, 54, 51, 0.6)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: TLC.paper,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: TLC.primaryMain,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: TLC.primaryMain,
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&.Mui-selected': {
            background: 'var(--app-sidebar-active-bg)',
            color: '#ffffff',
            '&:hover': {
              background: 'var(--app-sidebar-active-bg)',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
          fontWeight: 800,
          backgroundColor: alpha(TLC.gold, 0.18),
          color: TLC.forest,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(TLC.forest, 0.09)}`,
        },
      },
    },
  },
})

export default theme
